import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Grid,
  Table,
  TableRow
} from "semantic-ui-react";
import { AccordionTitleView, CommonTable, CustomButton, DropDown } from "../../../components";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { FacilityHeatTable, REFRIGERANT_UNIT_TYPES, UNIT_TYPES } from "../../../config/constants";
import { DEFAULT_REFIGERATION_SOURCE } from "../../../config/drawingConstants";
import "../facilityForms/facilityForm.scss";

const FacilityRefrigerantSimulation = ({
  refrigerantType,
  currentFacilityRefrigerantData,
  originalRefrigerantSourceData,
  setDeleteRefrigerantData,
  setVisibleDeleteRefrigerantModal,
  renderTableData = false,
  isFacilitySimulation,
  isViewMode = false
}: any) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [getDefaultUnit, setDefaultUnit] = useState<string>(UNIT_TYPES[0].value);
  const [defaultRefrigerantType, setDefaultRefrigerantType] = useState<number>(
    DEFAULT_REFIGERATION_SOURCE
  );
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const {
    formState: { errors: errorsFacilityRefrigerant },
    handleSubmit: handleSubmitFacilityRefrigerant,
    setValue,
    reset,
    control
  } = useForm();

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const selectedRefigurantType = useMemo(() => {
    return refrigerantType?.find((item: any) => item?.value === String(defaultRefrigerantType));
  }, [refrigerantType, defaultRefrigerantType]);

  const addFacilityRefrigerantData = (data: any) => {
    const price = data?.Price ? Number(data?.Price) : 0;
    let updateData = {
      ...data,
      Price: price,
      Volumes: Number(data?.Volumes),
      UnitType: getDefaultUnit,
      RefrigerantSource: Number(defaultRefrigerantType) || DEFAULT_REFIGERATION_SOURCE
    };
    if (enableEdit && editIndex !== null) {
      currentFacilityRefrigerantData[editIndex] = updateData;
    } else {
      currentFacilityRefrigerantData.push(updateData);
    }
    reset();
    setDefaultRefrigerantType(DEFAULT_REFIGERATION_SOURCE);
    setDefaultUnit(UNIT_TYPES[0].value);
    setEnableEdit(false);
    setEditIndex(null);
  };

  const groupRefrigerantData = (data: any) => {
    let groupIngredientData = data?.reduce((currentType: any, data: any, index: any) => {
      data.index = index;
      let checkDataIsAvailable = currentType?.find((other: any) => other.key === data.Year);
      if (currentType?.length === 0) {
        let facilityData = {
          key: data.Year,
          value: [data]
        };
        return [...currentType, facilityData];
      } else if (checkDataIsAvailable) {
        let currentIndex = currentType?.findIndex((other: any) => other.key === data.Year);
        let updatedArray = [];

        const updateArray = [...currentType[currentIndex].value, data];
        const updatedObject = {
          ...currentType[currentIndex],
          value: updateArray
        };
        updatedArray = [
          ...currentType.slice(0, currentIndex),
          updatedObject,
          ...currentType.slice(currentIndex + 1)
        ];
        return updatedArray;
      } else {
        let facilityData = {
          key: data.Year,
          value: [data]
        };

        return [...currentType, facilityData];
      }
    }, []);
    return groupIngredientData;
  };

  const setFacilityRefrigerantDetails = (data: any) => {
    setValue("Percentage", data?.Percentage);
    setValue("Price", data?.Price);
    setValue("Year", data?.Year);
    setValue("Volumes", data?.Volumes);
    setDefaultRefrigerantType(data?.RefrigerantSource);
    setDefaultUnit(data?.UnitType);
    setEditIndex(data?.index);
    setEnableEdit(true);
  };

  const renderTableRow = (
    dataView: any,
    rowSpan: any,
    totalValue: any,
    onDelete: any,
    showIcons: boolean
  ) => {
    const refrigerantTypeAsText = refrigerantType?.find(
      (item: any) => item?.value === String(dataView?.RefrigerantSource)
    )?.text;

    return (
      <TableRow key={dataView?.id || dataView?.index}>
        {rowSpan > 0 && (
          <Table.Cell rowSpan={rowSpan}>
            <p>{dataView?.Year ?? ""}</p>
          </Table.Cell>
        )}
        <Table.Cell>
          <p>{refrigerantTypeAsText ?? ""}</p>
        </Table.Cell>
        <Table.Cell>
          <p>
            {dataView?.Volumes ?? ""}
            <span className="unitTypeView">{dataView?.UnitType ?? ""}</span>
          </p>
        </Table.Cell>
         <Table.Cell>
          <p>{dataView?.Price?.toFixed(2) ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{totalValue?.toFixed(2)}</p>
        </Table.Cell>
        {!showIcons && !isViewMode ? (
          <Table.Cell>
            <Grid>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => setFacilityRefrigerantDetails(dataView)}
                />
              </Grid.Column>
              <Grid.Column computer={4} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={onDelete}
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        ) : null}
      </TableRow>
    );
  };

  const loadOriginalTableData = () => {
    const groupFacilityByYear = groupRefrigerantData(originalRefrigerantSourceData);
    return groupFacilityByYear?.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = dataView?.Volumes || 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteRefrigerantData(dataView);
          setVisibleDeleteRefrigerantModal(true);
        };
        return renderTableRow(dataView, isFirstRow ? rowCount : 0, totalValue, handleDelete, true);
      });
    });
  };

  const loadTableData = () => {
    const groupFacilityByYear = groupRefrigerantData(currentFacilityRefrigerantData);
    return groupFacilityByYear?.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = dataView?.Volumes || 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteRefrigerantData(dataView);
          setVisibleDeleteRefrigerantModal(true);
        };
        return renderTableRow(
          dataView,
          isFirstRow ? rowCount : 0,
          totalValue,
          handleDelete,
          renderTableData
        );
      });
    });
  };

  return (
    <>
      <Accordion>
        <AccordionTitle active={accordionEnabled} onClick={handleClick}>
          <AccordionTitleView accordionEnabled={accordionEnabled} title={"Refrigerant Sources"} />
        </AccordionTitle>
        <AccordionContent active={accordionEnabled}>
          <Grid>
            <Grid.Row divided>
              {isFacilitySimulation ? (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  {originalRefrigerantSourceData?.length > 0 && (
                    <CommonTable
                      tableHeaderData={FacilityHeatTable.filter((e: any) => e.name !== "")}
                      isSimulateView
                    >
                      {loadOriginalTableData()}
                    </CommonTable>
                  )}
                </Grid.Column>
              ) : null}
              <Grid.Column
                computer={isFacilitySimulation ? 8 : 16}
                tablet={16}
                mobile={16}
                className={!renderTableData ? "userBankDetailsMain" : "recipeTableViewMain"}
              >
                <div>
                  {!renderTableData ? (
                    <Grid>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <DropDown
                          labelName={"Source*"}
                          placeholder="Refrigerant Source"
                          currentData={refrigerantType}
                          defaultValue={selectedRefigurantType?.value || null}
                          customGridColumn={"customGridColomnType"}
                          handleChangeState={(e: any, { value }: any) => {
                            setDefaultRefrigerantType(value);
                          }}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <InputNumber
                          control={control}
                          errors={errorsFacilityRefrigerant.Year}
                          labelName={"Year*"}
                          placeholder="Year"
                          name="Year"
                          required={true}
                          errorMessage="Year is required"
                          isYearInput={true}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column
                        computer={8}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      />
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityRefrigerant.Volumes}
                          labelName={"Volumes consumed*"}
                          placeholder="Volumes"
                          name="Volumes"
                          required={true}
                          errorMessage="Volumes is required"
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={2} tablet={8} mobile={16}>
                        <Grid.Column computer={4} tablet={8} mobile={16}>
                          <DropDown
                            labelName={"Unit Type*"}
                            placeholder="Type"
                            currentData={REFRIGERANT_UNIT_TYPES}
                            defaultValue={getDefaultUnit}
                            customGridColumn={"customGridColomnType"}
                            handleChangeState={(e: any, { value }: any) => {
                              setDefaultUnit(value);
                            }}
                            disabled={isViewMode}
                          />
                        </Grid.Column>
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityRefrigerant.Price}
                          labelName={"Price per unit"}
                          placeholder="Price"
                          name="Price"
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        {enableEdit ? (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            title="Update"
                            onClick={handleSubmitFacilityRefrigerant((data: any) => {
                              addFacilityRefrigerantData(data);
                            })}
                            disabled={isViewMode}
                          />
                        ) : (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            icon="plus"
                            onClick={handleSubmitFacilityRefrigerant((data: any) => {
                              addFacilityRefrigerantData(data);
                            })}
                            disabled={isViewMode}
                          />
                        )}
                      </Grid.Column>
                    </Grid>
                  ) : null}

                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className={!renderTableData ? "productRegisterTable customTableBottom" : ""}
                  >
                    {currentFacilityRefrigerantData?.length > 0 && (
                      <CommonTable tableHeaderData={FacilityHeatTable}>
                        {loadTableData()}
                      </CommonTable>
                    )}
                  </Grid.Column>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </AccordionContent>
      </Accordion>
    </>
  );
};

export default FacilityRefrigerantSimulation;
